import { useState, useCallback, useMemo, useEffect } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Play, Copy, Check, Trash2, Settings, ChevronDown, ChevronUp,
  AlertCircle, XCircle
} from 'lucide-react'

interface RegexFlags {
  global: boolean
  ignoreCase: boolean
  multiline: boolean
  dotAll: boolean
  unicode: boolean
  sticky: boolean
}

const FLAG_PRESETS = [
  { name: 'Basic', flags: { global: false, ignoreCase: false, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { name: 'Case-insensitive', flags: { global: false, ignoreCase: true, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { name: 'Global', flags: { global: true, ignoreCase: false, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { name: 'Multiline', flags: { global: false, ignoreCase: false, multiline: true, dotAll: false, unicode: false, sticky: false } },
  { name: 'Full', flags: { global: true, ignoreCase: true, multiline: true, dotAll: true, unicode: false, sticky: false } },
]

const QUICK_PATTERNS = [
  { label: 'Email', pattern: '^[\\w.-]+@[\\w.-]+\\.[a-zA-Z]{2,}$', flags: { global: false, ignoreCase: true, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { label: 'URL', pattern: 'https?://[\\w.-]+\\.[a-zA-Z]{2,}(/[\\w.-]*)?', flags: { global: false, ignoreCase: true, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { label: 'IP Address', pattern: '^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$', flags: { global: false, ignoreCase: false, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { label: 'Phone', pattern: '\\+?[1-9]\\d{1,14}', flags: { global: false, ignoreCase: false, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { label: 'Date (YYYY-MM-DD)', pattern: '^\\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])$', flags: { global: false, ignoreCase: false, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { label: 'Hex Color', pattern: '^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$', flags: { global: false, ignoreCase: true, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { label: 'Number', pattern: '^-?\\d+\\.?\\d*$', flags: { global: false, ignoreCase: false, multiline: false, dotAll: false, unicode: false, sticky: false } },
  { label: 'Word', pattern: '\\b\\w+\\b', flags: { global: true, ignoreCase: false, multiline: false, dotAll: false, unicode: false, sticky: false } },
]

interface MatchResult {
  match: string
  index: number
  length: number
  groups: (string | undefined)[]
}

function buildFlags(flags: RegexFlags): string {
  let s = ''
  if (flags.global) s += 'g'
  if (flags.ignoreCase) s += 'i'
  if (flags.multiline) s += 'm'
  if (flags.dotAll) s += 's'
  if (flags.unicode) s += 'u'
  if (flags.sticky) s += 'y'
  return s
}

function App() {
  const { t } = useI18n()
  const [pattern, setPattern] = useState('')
  const [testText, setTestText] = useState('Hello world! Test email: user@example.com\nVisit https://www.example.com/page\nIP: 192.168.1.1\nDate: 2024-03-15')
  const [flags, setFlags] = useState<RegexFlags>({ global: false, ignoreCase: false, multiline: false, dotAll: false, unicode: false, sticky: false })
  const [showFlags, setShowFlags] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')

  const regexResult = useMemo(() => {
    if (!pattern) return { valid: false, matches: [] as MatchResult[], error: '' }
    try {
      const flagStr = buildFlags(flags)
      const regex = new RegExp(pattern, flagStr)
      const matches: MatchResult[] = []
      let match: RegExpExecArray | null
      const text = testText

      if (flags.global) {
        while ((match = regex.exec(text)) !== null) {
          matches.push({
            match: match[0],
            index: match.index,
            length: match[0].length,
            groups: match.slice(1),
          })
          if (match.index === regex.lastIndex) regex.lastIndex++
        }
      } else {
        match = regex.exec(text)
        if (match) {
          matches.push({
            match: match[0],
            index: match.index,
            length: match[0].length,
            groups: match.slice(1),
          })
        }
      }

      return { valid: true, matches, error: '' }
    } catch (e) {
      return { valid: false, matches: [] as MatchResult[], error: (e as Error).message }
    }
  }, [pattern, testText, flags])

  useEffect(() => {
    setError(regexResult.error)
  }, [regexResult.error])

  const highlightedText = useMemo(() => {
    if (!regexResult.valid || regexResult.matches.length === 0) return testText

    const parts: { text: string; isMatch: boolean; matchIndex?: number }[] = []
    let lastIndex = 0

    regexResult.matches.forEach((m, idx) => {
      if (m.index > lastIndex) {
        parts.push({ text: testText.slice(lastIndex, m.index), isMatch: false })
      }
      parts.push({ text: m.match, isMatch: true, matchIndex: idx })
      lastIndex = m.index + m.length
    })

    if (lastIndex < testText.length) {
      parts.push({ text: testText.slice(lastIndex), isMatch: false })
    }

    return parts
  }, [regexResult, testText])

  const toggleFlag = useCallback((key: keyof RegexFlags) => {
    setFlags(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const applyPreset = useCallback((preset: typeof FLAG_PRESETS[0]) => {
    setFlags(preset.flags)
  }, [])

  const applyPattern = useCallback((quick: typeof QUICK_PATTERNS[0]) => {
    setPattern(quick.pattern)
    setFlags(quick.flags)
  }, [])

  const copyPattern = useCallback(async () => {
    const flagStr = buildFlags(flags)
    const code = `/${pattern}/${flagStr}`
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }, [pattern, flags])

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('regexTester.title') || 'Regex Tester'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('regexTester.subtitle') || 'Test regular expressions in real-time. Match, highlight, and copy. '}
          <span className="text-primary font-medium">{t('regexTester.privacy') || '100% client-side'}</span>.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Quick Patterns */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            {t('regexTester.quickPatterns') || 'Quick Patterns'}
          </h3>
          <div className="flex flex-wrap gap-2">
            {QUICK_PATTERNS.map(quick => (
              <button
                key={quick.label}
                onClick={() => applyPattern(quick)}
                className="px-3 py-1.5 bg-muted text-foreground rounded-lg text-sm hover:bg-muted/80 transition-colors"
              >
                {quick.label}
              </button>
            ))}
          </div>
        </div>

        {/* Pattern Input */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Settings className="w-4 h-4 text-brand-primary" />
              {t('regexTester.pattern') || 'Pattern'}
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">/</span>
                <input
                  type="text"
                  value={pattern}
                  onChange={e => setPattern(e.target.value)}
                  placeholder={t('regexTester.patternPlaceholder') || 'Enter regex pattern...'}
                  className="w-full pl-8 pr-4 py-3 bg-background border border-border rounded-lg text-foreground text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/50"
                  spellCheck={false}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">/{buildFlags(flags)}</span>
              </div>
              <button
                onClick={() => setShowFlags(!showFlags)}
                className="px-4 py-3 bg-background border border-border rounded-lg text-foreground text-sm hover:bg-muted transition-colors inline-flex items-center gap-2"
              >
                {t('regexTester.flags') || 'Flags'}
                {showFlags ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Flags Panel */}
          {showFlags && (
            <div className="bg-background rounded-lg border border-border p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {FLAG_PRESETS.map(preset => (
                  <button
                    key={preset.name}
                    onClick={() => applyPreset(preset)}
                    className="px-3 py-1.5 bg-muted text-foreground rounded-lg text-xs hover:bg-muted/80 transition-colors"
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                {[
                  { key: 'global' as const, label: 'g (global)', desc: 'Find all matches' },
                  { key: 'ignoreCase' as const, label: 'i (ignoreCase)', desc: 'Case-insensitive' },
                  { key: 'multiline' as const, label: 'm (multiline)', desc: '^ and $ match line breaks' },
                  { key: 'dotAll' as const, label: 's (dotAll)', desc: ". matches newlines" },
                  { key: 'unicode' as const, label: 'u (unicode)', desc: 'Unicode support' },
                  { key: 'sticky' as const, label: 'y (sticky)', desc: 'Sticky matching' },
                ].map(flag => (
                  <label key={flag.key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flags[flag.key]}
                      onChange={() => toggleFlag(flag.key)}
                      className="w-4 h-4 rounded border-border text-brand-primary focus:ring-brand-primary"
                    />
                    <span className="text-sm text-foreground font-mono">{flag.label}</span>
                    <span className="text-xs text-muted-foreground">{flag.desc}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Copy Pattern */}
          {pattern && regexResult.valid && (
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-black/5 dark:bg-white/5 rounded-lg text-sm font-mono text-foreground">
                /{pattern}/{buildFlags(flags)}
              </code>
              <button
                onClick={copyPattern}
                className="px-3 py-2 bg-brand-primary text-white rounded-lg text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? (t('actions.copied') || 'Copied!') : (t('actions.copy') || 'Copy')}
              </button>
            </div>
          )}
        </div>

        {/* Test Text */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Play className="w-4 h-4 text-brand-primary" />
              {t('regexTester.testText') || 'Test Text'}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {regexResult.matches.length} {t('regexTester.matches') || 'matches'}
              </span>
              <button
                onClick={() => setTestText('')}
                className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          <textarea
            value={testText}
            onChange={e => setTestText(e.target.value)}
            placeholder={t('regexTester.testPlaceholder') || 'Enter text to test against...'}
            className="w-full h-40 px-4 py-3 bg-background border border-border rounded-lg text-foreground text-sm font-mono resize-y outline-none focus:ring-2 focus:ring-brand-primary/50 leading-relaxed"
            spellCheck={false}
          />

          {/* Highlighted Result */}
          {pattern && regexResult.valid && (
            <div className="bg-background border border-border rounded-lg p-4 min-h-[100px]">
              <div className="text-sm font-mono whitespace-pre-wrap leading-relaxed">
                {Array.isArray(highlightedText) ? highlightedText.map((part, i) => (
                  part.isMatch ? (
                    <mark
                      key={i}
                      className="bg-brand-primary/20 text-brand-primary px-0.5 rounded"
                      title={`Match ${(part.matchIndex || 0) + 1}`}
                    >
                      {part.text}
                    </mark>
                  ) : (
                    <span key={i}>{part.text}</span>
                  )
                )) : highlightedText}
              </div>
            </div>
          )}
        </div>

        {/* Matches Table */}
        {regexResult.matches.length > 0 && (
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              {t('regexTester.matches') || 'Matches'} ({regexResult.matches.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">#</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">{t('regexTester.match') || 'Match'}</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">{t('regexTester.index') || 'Index'}</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">{t('regexTester.length') || 'Length'}</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">{t('regexTester.groups') || 'Groups'}</th>
                  </tr>
                </thead>
                <tbody>
                  {regexResult.matches.map((m, idx) => (
                    <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                      <td className="py-2 px-3 text-muted-foreground">{idx + 1}</td>
                      <td className="py-2 px-3 font-mono text-foreground">{m.match}</td>
                      <td className="py-2 px-3 text-muted-foreground">{m.index}</td>
                      <td className="py-2 px-3 text-muted-foreground">{m.length}</td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {m.groups.length > 0 ? m.groups.map((g, i) => `(${i + 1}: ${g || '-'})`).join(', ') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Matches */}
        {pattern && regexResult.valid && regexResult.matches.length === 0 && (
          <div className="text-center py-8 bg-surface rounded-xl border border-border">
            <XCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('regexTester.noMatches') || 'No matches found'}</p>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">{t('security.note')}</p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
